import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

const AuthDebugger = ({ account, isAuthenticated }) => {
  const [showTokens, setShowTokens] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    const checkConfig = () => {
      const config = {
        dataverseUrl: process.env.REACT_APP_DATAVERSE_URL,
        clientId: process.env.REACT_APP_CLIENT_ID,
        tenantId: process.env.REACT_APP_TENANT_ID,
        redirectUri: process.env.REACT_APP_REDIRECT_URI,
      };

      const issues = [];
      if (!config.clientId || config.clientId.includes('PUT-YOUR')) {
        issues.push('Client ID not configured');
      }
      if (!config.tenantId || config.tenantId.includes('PUT-YOUR')) {
        issues.push('Tenant ID not configured');
      }
      if (!config.dataverseUrl) {
        issues.push('Dataverse URL not configured');
      }

      setDebugInfo({
        config,
        issues,
        account: account || {},
        isAuthenticated
      });
    };

    checkConfig();
  }, [account, isAuthenticated]);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-orange-500" />
          Authentication Debug Info
        </h3>
        <button
          onClick={() => setShowTokens(!showTokens)}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          {showTokens ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {showTokens ? 'Hide' : 'Show'} Details
        </button>
      </div>

      <div className="space-y-4">
        {/* Configuration Status */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Configuration Status:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              {debugInfo.config?.clientId && !debugInfo.config.clientId.includes('PUT-YOUR') ? 
                <CheckCircle className="w-4 h-4 text-green-500" /> : 
                <AlertCircle className="w-4 h-4 text-red-500" />
              }
              <span>Client ID: {debugInfo.config?.clientId ? 'Configured' : 'Missing'}</span>
            </div>
            <div className="flex items-center gap-2">
              {debugInfo.config?.tenantId && !debugInfo.config.tenantId.includes('PUT-YOUR') ? 
                <CheckCircle className="w-4 h-4 text-green-500" /> : 
                <AlertCircle className="w-4 h-4 text-red-500" />
              }
              <span>Tenant ID: {debugInfo.config?.tenantId ? 'Configured' : 'Missing'}</span>
            </div>
            <div className="flex items-center gap-2">
              {debugInfo.config?.dataverseUrl ? 
                <CheckCircle className="w-4 h-4 text-green-500" /> : 
                <AlertCircle className="w-4 h-4 text-red-500" />
              }
              <span>Dataverse URL: {debugInfo.config?.dataverseUrl ? 'Configured' : 'Missing'}</span>
            </div>
            <div className="flex items-center gap-2">
              {isAuthenticated ? 
                <CheckCircle className="w-4 h-4 text-green-500" /> : 
                <AlertCircle className="w-4 h-4 text-red-500" />
              }
              <span>Authentication: {isAuthenticated ? 'Success' : 'Failed'}</span>
            </div>
          </div>
        </div>

        {/* Issues */}
        {debugInfo.issues?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <h4 className="font-medium text-red-800 mb-2">Configuration Issues:</h4>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              {debugInfo.issues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Account Info */}
        {isAuthenticated && account && (
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Account Information:</h4>
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
              <div>Name: {account.name || 'N/A'}</div>
              <div>Username: {account.username || 'N/A'}</div>
              <div>Tenant: {account.tenantId || 'N/A'}</div>
            </div>
          </div>
        )}

        {/* Detailed Config (when expanded) */}
        {showTokens && (
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Configuration Details:</h4>
            <div className="bg-gray-100 rounded p-3 font-mono text-xs">
              <div>Client ID: {debugInfo.config?.clientId || 'Not set'}</div>
              <div>Tenant ID: {debugInfo.config?.tenantId || 'Not set'}</div>
              <div>Dataverse URL: {debugInfo.config?.dataverseUrl || 'Not set'}</div>
              <div>Redirect URI: {debugInfo.config?.redirectUri || 'Not set'}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthDebugger;
